"use client";

import { markAccountSwitched, setApiAccessToken } from "@/lib/api-client";
import { clearWorkspacePreferenceCache } from "@/lib/workspace-preferences";

let redirectPromise: Promise<void> | null = null;

export function logoutAndRedirect() {
  if (redirectPromise) {
    return redirectPromise;
  }

  redirectPromise = (async () => {
    setApiAccessToken(null, { source: "logout" });
    clearWorkspacePreferenceCache();
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname + window.location.search;
      const returnTo = currentPath && currentPath !== "/" && currentPath !== "/login"
        ? `?returnTo=${encodeURIComponent(currentPath)}`
        : "";
      window.location.href = `/login${returnTo}`;
    }
  })();

  return redirectPromise;
}

let pendingCookieSync: Promise<void> | null = null;

/**
 * Pushes a token the API rotated (`X-Refreshed-Token`) into the httpOnly
 * session cookie so `middleware.ts` and server components see the new expiry.
 * localStorage alone is not enough: the cookie keeps the login-time expiry and
 * full-page navigations would bounce to /login after it passes.
 *
 * Best effort: a failed sync is logged and retried on the next refresh. The
 * token is already live in memory, so API calls keep working meanwhile.
 */
export function syncSessionCookie(accessToken: string): Promise<void> {
  if (pendingCookieSync) {
    return pendingCookieSync;
  }

  pendingCookieSync = (async () => {
    try {
      const response = await fetch("/api/session", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken }),
        cache: "no-store",
      });
      if (response.status === 409 && (await isSubjectMismatch(response))) {
        // The cookie now belongs to an account another tab signed in as. This
        // tab's token is the odd one out: freeze it and ask for a reload.
        markAccountSwitched({ subject: "unknown" });
        return;
      }
      if (!response.ok) {
        console.error(`Session cookie refresh failed (${response.status})`);
      }
    } catch (error) {
      console.error("Session cookie refresh failed", error);
    } finally {
      pendingCookieSync = null;
    }
  })();

  return pendingCookieSync;
}

async function isSubjectMismatch(response: Response): Promise<boolean> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return body?.error === "session_subject_mismatch";
  } catch {
    return false;
  }
}
